import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/**
 * Computes the pagination range with ellipsis matching the reference design:
 * e.g. [1, 2, 3, '...', 9, 10]
 */
export function getPaginationRange(currentPage: number, totalPages: number): (number | '...')[] {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Matches reference image layout for totalPages <= 10:
  // Row 1 (page 1):  [1, 2, 3, '...', 9, 10]
  // Row 2 (page 2):  [1, 2, 3, '...', 9, 10]
  // Row 3 (page 10): [1, 2, 3, '...', 9, 10]
  if (totalPages <= 10) {
    if (currentPage <= 3 || currentPage >= totalPages - 1) {
      return [1, 2, 3, '...', totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  }

  // For larger page counts (> 10):
  if (currentPage <= 3) {
    return [1, 2, 3, '...', totalPages - 1, totalPages];
  } else if (currentPage >= totalPages - 2) {
    return [1, 2, '...', totalPages - 2, totalPages - 1, totalPages];
  } else {
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  }
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
}: PaginationProps) {
  if (totalPages <= 0) return null;

  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= totalPages;
  const pageRange = getPaginationRange(currentPage, totalPages);

  return (
    <nav
      aria-label="Applications pagination"
      className={`flex items-center justify-center gap-2 sm:gap-3 select-none ${className}`}
    >
      {/* Previous button */}
      <button
        type="button"
        disabled={isPrevDisabled}
        onClick={() => !isPrevDisabled && onPageChange(currentPage - 1)}
        className={`inline-flex items-center justify-center gap-1.5 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-lg border text-xs sm:text-[13px] font-bold tracking-wider uppercase transition-all ${
          isPrevDisabled
            ? 'bg-[#F6F7F6] border-[#E5E8E5] text-[#B5BCB7] cursor-not-allowed shadow-none'
            : 'bg-white border-[#D3DAD7] text-[#132A21] hover:bg-[#F2F5F2] hover:border-[#C4CCC6] cursor-pointer'
        }`}
        aria-label="Previous page"
      >
        <svg
          className="w-3.5 h-3.5 flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        <span>PREVIOUS</span>
      </button>

      {/* Segmented page numbers */}
      <div className="inline-flex items-center border border-[#D3DAD7] rounded-lg overflow-hidden bg-white divide-x divide-[#D3DAD7]">
        {pageRange.map((item, idx) => {
          if (item === '...') {
            return (
              <span
                key={`ellipsis-${idx}`}
                className="w-9 h-[38px] sm:w-10 sm:h-[40px] flex items-center justify-center text-xs sm:text-sm font-bold text-[#132A21] select-none bg-white"
              >
                ...
              </span>
            );
          }

          const pageNum = item as number;
          const isActive = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              type="button"
              onClick={() => onPageChange(pageNum)}
              aria-current={isActive ? 'page' : undefined}
              className={`w-9 h-[38px] sm:w-10 sm:h-[40px] flex items-center justify-center text-xs sm:text-sm font-semibold transition-colors ${
                isActive
                  ? 'bg-[#DAE4DC] text-[#132A21] font-bold cursor-default'
                  : 'bg-white text-[#132A21] hover:bg-[#F2F5F2] cursor-pointer'
              }`}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next button */}
      <button
        type="button"
        disabled={isNextDisabled}
        onClick={() => !isNextDisabled && onPageChange(currentPage + 1)}
        className={`inline-flex items-center justify-center gap-1.5 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-lg border text-xs sm:text-[13px] font-bold tracking-wider uppercase transition-all ${
          isNextDisabled
            ? 'bg-[#F6F7F6] border-[#E5E8E5] text-[#B5BCB7] cursor-not-allowed shadow-none'
            : 'bg-white border-[#D3DAD7] text-[#132A21] hover:bg-[#F2F5F2] hover:border-[#C4CCC6] cursor-pointer'
        }`}
        aria-label="Next page"
      >
        <span>NEXT</span>
        <svg
          className="w-3.5 h-3.5 flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </nav>
  );
}
